import { useEffect, useMemo, useState } from 'react';
import type { EvidenceLabel } from '@sr-test-wizard/shared';
import {
  evidenceLabelsApi,
  type CreateEvidenceLabelInput,
  type UpdateEvidenceLabelInput,
} from '../../lib/evidenceLabelsApi';

/**
 * Settings UI for the project-wide evidence-label taxonomy. Mirrors
 * `TestGuidancePage` - list / add / edit / delete plus a text filter -
 * because the operators are already trained on that shape. Both surfaces
 * write through to a JSON store managed by the server.
 *
 * `isVisual` is the only field with downstream behavioural impact: the
 * per-frame / per-image tagger only ever picks from `isVisual=true`
 * entries. The other entries (network, source-code-review, measurement,
 * client-action, …) are kept for completeness so the Settings page mirrors
 * the full Jira "Indicator label" table.
 */

const KEY_RE = /^[a-zA-Z0-9_-]+$/;

const FIELD_CAPS = {
  name: 120,
  definition: 4000,
  evidenceType: 200,
} as const;

interface DraftValues {
  key: string;
  name: string;
  definition: string;
  evidenceType: string;
  isVisual: boolean;
}

const EMPTY_DRAFT: DraftValues = {
  key: '',
  name: '',
  definition: '',
  evidenceType: '',
  isVisual: true,
};

function draftFromEntry(entry: EvidenceLabel): DraftValues {
  return {
    key: entry.key,
    name: entry.name,
    definition: entry.definition,
    evidenceType: entry.evidenceType,
    isVisual: entry.isVisual,
  };
}

interface FieldEditorProps {
  label: string;
  value: string;
  cap: number;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  onChange: (next: string) => void;
}

function FieldEditor({ label, value, cap, rows = 3, placeholder, disabled, onChange }: FieldEditorProps) {
  return (
    <div className="flex-col" style={{ gap: 4 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <label className="muted small" style={{ minWidth: 0 }}>
          {label}
        </label>
        <span className="spacer" />
        <span className="muted small mono">
          {value.length}/{cap}
        </span>
      </div>
      <textarea
        value={value}
        maxLength={cap}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        style={{ resize: 'vertical', minHeight: rows * 24 }}
        disabled={disabled}
      />
    </div>
  );
}

interface LabelFormProps {
  draft: DraftValues;
  busy: boolean;
  error: string | null;
  mode: 'create' | 'edit';
  onChange: (next: DraftValues) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

function LabelForm({ draft, busy, error, mode, onChange, onCancel, onSubmit }: LabelFormProps) {
  const keyValid = KEY_RE.test(draft.key.trim());
  const canSave = keyValid && draft.definition.trim().length > 0;
  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="row" style={{ gap: 8 }}>
        <h3 style={{ margin: 0 }}>
          {mode === 'create' ? 'Add label' : `Edit ${draft.key || 'label'}`}
        </h3>
        <span className="spacer" />
        <button type="button" className="btn small btn-ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
      <div className="flex-col" style={{ gap: 10, marginTop: 10 }}>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <label className="muted small" style={{ minWidth: 110 }}>
            Key
          </label>
          <input
            value={draft.key}
            maxLength={40}
            onChange={(e) => onChange({ ...draft, key: e.target.value })}
            placeholder="gp-base-game"
            style={{ flex: 1, fontFamily: 'monospace' }}
            disabled={busy}
          />
          {!keyValid && draft.key.trim() ? (
            <span className="badge small bad" title="Letters, digits, '-' and '_' only">
              Invalid key
            </span>
          ) : (
            <span className="muted small">Matches the Jira label exactly</span>
          )}
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <label className="muted small" style={{ minWidth: 110 }}>
            Name
          </label>
          <input
            value={draft.name}
            maxLength={FIELD_CAPS.name}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            placeholder="Base round lifecycle"
            style={{ flex: 1 }}
            disabled={busy}
          />
          <span className="muted small mono">
            {draft.name.length}/{FIELD_CAPS.name}
          </span>
        </div>
        <FieldEditor
          label="Definition (used verbatim by the tagger + aggregator)"
          value={draft.definition}
          cap={FIELD_CAPS.definition}
          rows={6}
          placeholder='Verbatim Jira definition. The tagger reads it to decide which frames qualify, and the aggregator quotes it in the "Why these frames" prompt header.'
          disabled={busy}
          onChange={(v) => onChange({ ...draft, definition: v })}
        />
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <label className="muted small" style={{ minWidth: 110 }}>
            Evidence type
          </label>
          <input
            value={draft.evidenceType}
            maxLength={FIELD_CAPS.evidenceType}
            onChange={(e) => onChange({ ...draft, evidenceType: e.target.value })}
            placeholder="Video"
            style={{ flex: 1 }}
            disabled={busy}
          />
          <span className="muted small mono">
            {draft.evidenceType.length}/{FIELD_CAPS.evidenceType}
          </span>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <label className="muted small" style={{ minWidth: 110 }}>
            Visual?
          </label>
          <label className="row" style={{ gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={draft.isVisual}
              onChange={(e) => onChange({ ...draft, isVisual: e.target.checked })}
              disabled={busy}
            />
            <span className="muted small">
              Tick when this label can be assigned to a video frame or screenshot
              by the second-pass tagger. Non-visual labels (links, measurements,
              client actions) are kept for completeness but never appear on a
              frame chip.
            </span>
          </label>
        </div>
        <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <span className="spacer" />
          <button
            type="button"
            className="btn small btn-primary"
            onClick={onSubmit}
            disabled={busy || !canSave}
            title={!canSave ? 'Key must match [a-zA-Z0-9_-]+ and definition must be non-empty' : undefined}
          >
            {busy ? 'Saving…' : mode === 'create' ? 'Add label' : 'Save changes'}
          </button>
        </div>
        {error && (
          <div className="muted small" style={{ color: 'var(--bad)' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

interface EntryCardProps {
  entry: EvidenceLabel;
  onEdit: () => void;
  onDelete: () => void;
}

function EntryCard({ entry, onEdit, onDelete }: EntryCardProps) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <span className="badge small mono">{entry.key}</span>
        <strong style={{ fontSize: 14 }}>{entry.name || '(no name)'}</strong>
        <span className="spacer" />
        {entry.isVisual ? (
          <span
            className="badge small good"
            title="The per-frame / per-image tagger may assign this label."
          >
            visual
          </span>
        ) : (
          <span
            className="badge small"
            title="Stored for completeness but never assigned to a frame or image."
          >
            non-visual
          </span>
        )}
        {entry.evidenceType && (
          <span className="badge small" title={`Evidence type: ${entry.evidenceType}`}>
            {entry.evidenceType}
          </span>
        )}
        <span
          className="muted small"
          title={`Last edited ${new Date(entry.updatedAt).toLocaleString()}`}
        >
          {new Date(entry.updatedAt).toLocaleDateString()}
        </span>
        <button type="button" className="btn small" onClick={onEdit}>
          Edit
        </button>
        <button
          type="button"
          className="btn small"
          onClick={onDelete}
          style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}
        >
          Delete
        </button>
      </div>
      <div className="flex-col" style={{ gap: 6, marginTop: 8 }}>
        <div className="muted small mono">Definition</div>
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{entry.definition}</div>
      </div>
    </div>
  );
}

export function EvidenceLabelsPage() {
  const [entries, setEntries] = useState<EvidenceLabel[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [filter, setFilter] = useState('');
  const [onlyVisual, setOnlyVisual] = useState(false);

  const [adding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState<DraftValues>(EMPTY_DRAFT);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftValues>(EMPTY_DRAFT);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await evidenceLabelsApi.list();
      setEntries(data.entries);
    } catch (err) {
      setLoadError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return entries.filter((e) => {
      if (onlyVisual && !e.isVisual) return false;
      if (!q) return true;
      const haystack = [e.key, e.name, e.definition, e.evidenceType].join('\n').toLowerCase();
      return haystack.includes(q);
    });
  }, [entries, filter, onlyVisual]);

  const startAdd = () => {
    setAdding(true);
    setAddDraft(EMPTY_DRAFT);
    setAddError(null);
    setEditingId(null);
  };

  const cancelAdd = () => {
    setAdding(false);
    setAddDraft(EMPTY_DRAFT);
    setAddError(null);
  };

  const buildPayload = (d: DraftValues): CreateEvidenceLabelInput => ({
    key: d.key.trim(),
    name: d.name.trim(),
    definition: d.definition.trim(),
    evidenceType: d.evidenceType.trim(),
    isVisual: d.isVisual,
  });

  const submitAdd = async () => {
    setAddBusy(true);
    setAddError(null);
    try {
      const created = await evidenceLabelsApi.create(buildPayload(addDraft));
      setEntries((prev) => [...prev, created]);
      cancelAdd();
    } catch (err) {
      setAddError((err as Error).message);
    } finally {
      setAddBusy(false);
    }
  };

  const startEdit = (entry: EvidenceLabel) => {
    setEditingId(entry.id);
    setEditDraft(draftFromEntry(entry));
    setEditError(null);
    setAdding(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(EMPTY_DRAFT);
    setEditError(null);
  };

  const submitEdit = async (id: string) => {
    setEditBusy(true);
    setEditError(null);
    try {
      const patch: UpdateEvidenceLabelInput = buildPayload(editDraft);
      const updated = await evidenceLabelsApi.update(id, patch);
      setEntries((prev) => prev.map((e) => (e.id === id ? updated : e)));
      cancelEdit();
    } catch (err) {
      setEditError((err as Error).message);
    } finally {
      setEditBusy(false);
    }
  };

  const removeOne = async (entry: EvidenceLabel) => {
    if (!window.confirm(`Delete evidence label ${entry.key}?`)) return;
    try {
      await evidenceLabelsApi.remove(entry.id);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      if (editingId === entry.id) cancelEdit();
    } catch (err) {
      setLoadError((err as Error).message);
    }
  };

  return (
    <div className="flex-col" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Evidence labels</h1>
        <span className="spacer" />
        <label className="row" style={{ gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={onlyVisual}
            onChange={(e) => setOnlyVisual(e.target.checked)}
          />
          <span className="muted small">Visual only</span>
        </label>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by key, name or definition…"
          style={{ width: 260 }}
        />
        {!adding && (
          <button type="button" className="btn small btn-primary" onClick={startAdd}>
            + Add label
          </button>
        )}
      </div>

      <p className="muted small" style={{ marginTop: 0 }}>
        Closed vocabulary used to tag every video frame and screenshot. The
        test aggregator filters spliced evidence to whatever the current
        TLIB's labels intersect, so a base-game test no longer drowns in
        help-overlay frames. Edit definitions in place; the next frame /
        image analysis pass picks up the change without a code deploy.
      </p>

      {loadError && (
        <div className="card" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}>
          {loadError}
        </div>
      )}

      {adding && (
        <LabelForm
          draft={addDraft}
          busy={addBusy}
          error={addError}
          mode="create"
          onChange={setAddDraft}
          onCancel={cancelAdd}
          onSubmit={() => void submitAdd()}
        />
      )}

      {loading ? (
        <div className="muted">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            No labels yet. Click "Add label" to seed your first entry.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="muted">No labels match the current filter.</div>
      ) : (
        <div className="flex-col" style={{ gap: 10 }}>
          {filtered.map((entry) =>
            editingId === entry.id ? (
              <LabelForm
                key={entry.id}
                draft={editDraft}
                busy={editBusy}
                error={editError}
                mode="edit"
                onChange={setEditDraft}
                onCancel={cancelEdit}
                onSubmit={() => void submitEdit(entry.id)}
              />
            ) : (
              <EntryCard
                key={entry.id}
                entry={entry}
                onEdit={() => startEdit(entry)}
                onDelete={() => void removeOne(entry)}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}
